// assets/metaobject-swatches.js
// Swatches for products sharing the same custom.model_code.
// Group members are fetched at render time via the Storefront API.

if (!customElements.get('metaobject-swatches')) {
  // Module-level cache so navigating between siblings (soft-swap) doesn't refetch the group.
  const groupCache = new Map(); // key: modelCode -> array of product nodes

  class MetaobjectSwatches extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleHoverPrefetch = this.handleHoverPrefetch.bind(this);

      // In-memory cache for fetched product-info HTML (soft-swap)
      this._cache = new Map(); // key: productUrl -> { html, at }
      this._maxCacheEntries = 10;

      // One in-flight controller for last-writer-wins
      this._controller = null;
      this._inFlightUrl = null;

      this._lastSelectedUrl = null;

      this._debounceTimer = null;
      this._debounceDelay = 60; // ms

      this._initialized = false;
    }

    async connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._modelCode = this.dataset.modelCode || '';
      this._token = this.dataset.storefrontToken || '';
      this._apiVersion = this.dataset.apiVersion || '2026-01';
      this._currentHandle = this.dataset.currentHandle || '';

      if (!this._modelCode || !this._token || this._token.indexOf('PUT_YOUR') === 0) {
        return; // not configured; stay hidden
      }

      let nodes;
      try {
        nodes = await this._fetchGroup(this._modelCode);
      } catch (_) {
        return; // network/API error; stay hidden
      }

      // Only worth showing if there's at least one sibling besides the current product.
      if (!nodes || nodes.length < 2) return;

      this._buildSwatches(nodes);
      this._wireButtons();
      this._reveal();
    }

    disconnectedCallback() {
      if (!this.buttons) return;
      this.buttons.forEach((button) => {
        button.removeEventListener('click', this.handleClick);
        button.removeEventListener('keydown', this.handleKeyDown);
        button.removeEventListener('mouseenter', this.handleHoverPrefetch);
        button.removeEventListener('focus', this.handleHoverPrefetch);
      });
      this._abortInFlight();
    }

    // ---------- Fetch group via Storefront API ----------

    async _fetchGroup(code) {
      if (groupCache.has(code)) return groupCache.get(code);

      const query = `
  query GroupSwatches($code: String!, $first: Int!) {
    search(
      query: "",
      first: $first,
      types: [PRODUCT],
      productFilters: [{ productMetafield: { namespace: "custom", key: "model_code", value: $code } }]
    ) {
      nodes {
        ... on Product {
          handle
          title
          onlineStoreUrl
          featuredImage { url(transform: { maxWidth: 480 }) altText }
          optName: metafield(namespace: "custom", key: "option_name") { value }
        }
      }
    }
  }
`;

      const res = await fetch(`/api/${this._apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this._token,
        },
        body: JSON.stringify({ query, variables: { code, first: 30 } }),
      });

      if (!res.ok) throw new Error('Storefront API ' + res.status);
      const json = await res.json();
      const nodes = json?.data?.search?.nodes ?? [];
      // Optional: to control swatch order, add a custom.option_position metafield
      // to each product and sort here, e.g.:
      // nodes.sort((a, b) => (+a.optPos?.value || 0) - (+b.optPos?.value || 0));

      groupCache.set(code, nodes);
      return nodes;
    }

    // ---------- Build swatch tiles (same markup/classes as before) ----------

    _buildSwatches(nodes) {
      const frag = document.createDocumentFragment();

      nodes.forEach((node) => {
        const url = node.onlineStoreUrl || `/products/${node.handle}`;
        const label = (node.optName && node.optName.value) ? node.optName.value : node.title;
        const isActive = node.handle === this._currentHandle;

        const a = document.createElement('a');
        a.className = 'Swatch' + (isActive ? ' is-active' : '');
        a.href = url;
        a.setAttribute('role', 'radio');
        a.setAttribute('aria-checked', isActive ? 'true' : 'false');
        a.tabIndex = isActive ? 0 : -1;
        a.setAttribute('data-metaobject-swatch', '');
        a.dataset.productUrl = url;
        a.dataset.productHandle = node.handle;
        if (label) a.title = label;

        if (node.featuredImage && node.featuredImage.url) {
          const img = document.createElement('img');
          img.className = 'Swatch__img';
          img.src = node.featuredImage.url;
          img.alt = node.featuredImage.altText || label || '';
          img.loading = 'lazy';
          a.appendChild(img);
        }

        if (label) {
          const span = document.createElement('span');
          span.className = 'Swatch__label';
          span.textContent = label;
          a.appendChild(span);
        }

        frag.appendChild(a);
      });

      this.innerHTML = '';
      this.appendChild(frag);
    }

    _reveal() {
      const wrap = this.closest('.SwatchWrap');
      if (wrap) wrap.hidden = false;
    }

    // ---------- Wire interactions (unchanged behavior) ----------

    _wireButtons() {
      this.buttons = Array.from(this.querySelectorAll('[data-metaobject-swatch]'));
      if (!this.buttons.length) return;

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleClick);
        button.addEventListener('keydown', this.handleKeyDown);
        button.addEventListener('mouseenter', this.handleHoverPrefetch, { passive: true });
        button.addEventListener('focus', this.handleHoverPrefetch, { passive: true });
      });

      const activeButton =
        this.buttons.find((button) => button.classList.contains('is-active')) || this.buttons[0];
      if (activeButton) {
        this.setActiveButton(activeButton, { focus: false, emit: false });
      }
    }

    // ---------- UI state ----------

    setActiveButton(button, { focus = false, emit = false, originEvent = null } = {}) {
      if (!button) return;

      const alreadyActive = button.classList.contains('is-active');
      if (alreadyActive && !emit) {
        button.tabIndex = 0;
        if (focus) button.focus();
        return;
      }

      this.buttons.forEach((btn) => {
        const isActive = btn === button;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        btn.tabIndex = isActive ? 0 : -1;
      });

      if (focus) {
        button.focus();
      }

      if (emit) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this.publishSelection(button, originEvent);
        }, this._debounceDelay);
      }
    }

    handleClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      if (!button) return;

      if (button.classList.contains('is-active')) {
        button.focus();
        return;
      }

      this.setActiveButton(button, { focus: false, emit: true, originEvent: event });
    }

    handleKeyDown(event) {
      const button = event.currentTarget;
      const { key } = event;

      if (key === ' ' || key === 'Spacebar') {
        event.preventDefault();
        button.click();
        return;
      }
      if (key === 'Enter') return;

      if (!this.buttons || !this.buttons.length) return;

      const currentIndex = this.buttons.indexOf(button);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      if (key === 'ArrowLeft' || key === 'ArrowUp') {
        event.preventDefault();
        nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
      } else if (key === 'ArrowRight' || key === 'ArrowDown') {
        event.preventDefault();
        nextIndex = (currentIndex + 1) % this.buttons.length;
      } else if (key === 'Home') {
        event.preventDefault();
        nextIndex = 0;
      } else if (key === 'End') {
        event.preventDefault();
        nextIndex = this.buttons.length - 1;
      } else {
        return;
      }

      const nextButton = this.buttons[nextIndex];
      if (nextButton) {
        this.setActiveButton(nextButton, { focus: true, emit: true, originEvent: event });
      }
    }

    // ---------- Publish selection (theme path or fast fallback) ----------

    publishSelection(button, originEvent) {
      const productUrl = button?.dataset?.productUrl;
      if (!productUrl) return;

      if (this._lastSelectedUrl === productUrl && !this._inFlightUrl) {
        return;
      }
      this._lastSelectedUrl = productUrl;

      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.pendingRequestUrl = productUrl;
      }

      if (window.FoxTheme && FoxTheme.pubsub && typeof FoxTheme.pubsub.publish === 'function') {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event: originEvent || new Event('click', { bubbles: true }),
            target: button,
            selectedOptionValues: [],
          },
        });
        return;
      }

      this._softSwapTo(productUrl).catch(() => {
        window.location.assign(productUrl);
      });
    }

    // ---------- Prefetch (hover/focus) ----------

    handleHoverPrefetch(event) {
      const url = event.currentTarget?.dataset?.productUrl;
      if (!url) return;
      if (!this._cache.has(url) && this._inFlightUrl !== url) {
        this._prefetch(url).catch(() => {});
      }
    }

    async _prefetch(url) {
      const html = await this._fetchProductInfoHTML(url);
      if (html) {
        this._putCache(url, html);
      }
    }

    // ---------- Soft swap implementation ----------

    async _softSwapTo(url) {
      let html = this._cache.get(url)?.html;
      if (!html) {
        html = await this._fetchProductInfoHTML(url);
        if (!html) throw new Error('No product-info found');
        this._putCache(url, html);
      }

      const current = document.querySelector('product-info');
      if (!current) throw new Error('<product-info> not found on page');

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html.trim();
      const next = wrapper.querySelector('product-info');
      if (!next) throw new Error('product-info not in fragment');

      const rect = current.getBoundingClientRect();
      current.style.minHeight = rect.height + 'px';

      current.replaceWith(next);

      requestAnimationFrame(() => {
        next.style.minHeight = '';
      });

      try {
        history.pushState({}, '', url);
      } catch (_) {}

      document.dispatchEvent(new CustomEvent('product:info-swapped', { detail: { url } }));
    }

    async _fetchProductInfoHTML(url) {
      // Abort prior request if any (last-writer wins)
      this._abortInFlight();
      const controller = new AbortController();
      this._controller = controller;
      this._inFlightUrl = url;

      let text;
      try {
        const res = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!res.ok) throw new Error('Bad status ' + res.status);
        text = await res.text();
      } catch (err) {
        // A superseded prefetch/swap aborts the previous fetch — expected, ignore it.
        if (err && err.name === 'AbortError') return null;
        throw err;
      } finally {
        if (this._inFlightUrl === url) this._inFlightUrl = null;
      }

      // Parse and extract product-info outerHTML
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const node = doc.querySelector('product-info');
      if (!node) return null;

      return node.outerHTML;
    }

    _abortInFlight() {
      if (this._controller) {
        try { this._controller.abort(); } catch (_) {}
      }
      this._controller = null;
      this._inFlightUrl = null;
    }

    _putCache(url, html) {
      if (!this._cache.has(url) && this._cache.size >= this._maxCacheEntries) {
        const firstKey = this._cache.keys().next().value;
        this._cache.delete(firstKey);
      }
      this._cache.set(url, { html, at: Date.now() });
    }
  }

  customElements.define('metaobject-swatches', MetaobjectSwatches);
}
